import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { NutritionProductPlan, PlannedNutrition } from "../../services/PlansService";
import { useContext, useEffect, useState } from "react";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import NutritionProductsTable from "./NutritionProductsTable";
import Settings from "@mui/icons-material/Settings";
import { AppContext } from "../../contexts/AppContext";
import { getProductList, getTotalNutrition, getTotalSectionNutrition } from "../../utils/NutritionUtils";
import styled from "@mui/material/styles/styled";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import posthog from "posthog-js";
import NutritionProductsList from "../nutrition/NutritionProductsList";
import NutritionSummaryTabs from "../nutrition/NutritionSummaryTabs";

dayjs.extend(localizedFormat);

const ParagraphLine = styled("span")(({ theme }) => ({
    verticalAlign: "baseline",
    height: 1,
    flexGrow: 1,
    flexShrink: 1,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: "0.5em",
    marginRight: "0.5em"
}));

const TitleBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    marginTop: theme.spacing(0),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
}));

export interface NutritionBoxProps {
    totalTime: number;
    totalNutrition: PlannedNutrition[];
    sectionNutrition: NutritionProductPlan[];
    setSectionNutrition: (sectionNutrition: NutritionProductPlan[]) => void;
    readOnly?: boolean;
    ownerId?: string;
}

const NutritionBox: React.FC<NutritionBoxProps> = observer(({ totalTime, totalNutrition, sectionNutrition, setSectionNutrition, readOnly, ownerId }) => {

    const intl = useIntl();

    const { userService, nutritionProductsService } = useContext(AppContext);

    const [nutritionTableOpen, setNutritionTableOpen] = useState<boolean>(false);

    const sectionNutritionValues = getTotalSectionNutrition(sectionNutrition, nutritionProductsService.nutritionProducts);
    const totalNutritionValues = getTotalNutrition(totalNutrition, nutritionProductsService.nutritionProducts);
    const totalNutritionValuesPerHour = {
        water: totalTime ? totalNutritionValues.water / (totalTime / 60) : 0,
        calories: totalTime ? totalNutritionValues.calories / (totalTime / 60) : 0,
        carbs: totalTime ? totalNutritionValues.carbs / (totalTime / 60) : 0,
        protein: totalTime ? totalNutritionValues.protein / (totalTime / 60) : 0,
        fat: totalTime ? totalNutritionValues.fat / (totalTime / 60) : 0,
        sodium: totalTime ? totalNutritionValues.sodium / (totalTime / 60) : 0,
        caffeine: totalTime ? totalNutritionValues.caffeine / (totalTime / 60) : 0
    }
    const nutritionValues: Record<string, {
        water: number,
        calories: number,
        carbs: number,
        protein: number,
        fat: number,
        sodium: number,
        caffeine: number
    }> = {
        section: sectionNutritionValues,
        plan: totalNutritionValues,
        hour: totalNutritionValuesPerHour
    }

    const productList = getProductList(totalNutrition, nutritionProductsService.nutritionProducts);

    useEffect(() => {
        const userId = ownerId || userService.user?.id;
        if (!userId) return;
        nutritionProductsService.loadNutritionProducts(userId);
    }, [nutritionProductsService, userService.user?.id, ownerId]);

    return (
        <Box p={1}>
            <TitleBox display="flex" alignItems="center" justifyContent="space-between" marginY={1}>
                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "plans.nutritionTitle" })}</Typography>
                {!readOnly && (
                    <Button startIcon={<Settings />} onClick={() => {
                        posthog.capture('nutrition_settings_click');
                        setNutritionTableOpen(true)
                    }} size="small">
                        {intl.formatMessage({ id: "plans.nutritionProducts" })}
                    </Button>
                )}
            </TitleBox>
            <NutritionProductsList
                nutrition={sectionNutrition}
                setNutrition={setSectionNutrition}
                onOpenProductsTable={() => setNutritionTableOpen(true)}
                readOnly={readOnly}
            />

            <NutritionSummaryTabs
                nutritionValues={nutritionValues}
                tabs={[
                    { value: "section", labelId: "data.totalNutritionForSection" },
                    { value: "plan", labelId: "data.totalNutritionForPlan" },
                    { value: "hour", labelId: "data.totalNutritionPerHour" }
                ]}
                defaultTab="section"
            />


            <Box textAlign="center" marginY={1}>
                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "plans.productList" })}</Typography>
            </Box>
            <Divider />
            {productList.map((product, index) => (
                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1} key={index}>
                    <Typography variant="body2">{product.name}</Typography>
                    <ParagraphLine />
                    <Typography variant="body2">{product.quantity}</Typography>
                </Box>
            ))}
            {
                productList.length === 0 && <Box textAlign="center" marginY={1}>
                    <Typography variant="body2">{intl.formatMessage({ id: "plans.noProducts" })}</Typography>
                </Box>
            }

            <Dialog
                open={nutritionTableOpen}
                onClose={() => {
                    posthog.capture('nutrition_table_close');
                    setNutritionTableOpen(false)
                    nutritionProductsService.loadNutritionProducts(userService.user?.id || "")
                }}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    {intl.formatMessage({ id: "plans.nutritionProducts" })}
                </DialogTitle>
                <DialogContent>
                    <NutritionProductsTable />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        posthog.capture('nutrition_table_close');
                        setNutritionTableOpen(false)
                        nutritionProductsService.loadNutritionProducts(userService.user?.id || "")
                    }}>
                        {intl.formatMessage({ id: "action.done" })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default NutritionBox;